import {
  GetChatDetailFragment,
  GetChatDetailFragmentDoc,
} from '@graphql/generated';
import { useApolloClient } from '@hooks/useApolloClient';

export const useGetChatFromCache = (chatId: string | undefined) => {
  const { client } = useApolloClient();

  // TA-10885: `readFragment` is all-or-nothing by default — it returns null as
  // soon as a single GetChatDetail field is missing from the cached entity. A
  // chat the user has only ever seen in the list was written by
  // `listChatsPagination`, whose node carries a smaller set of fields (no
  // `avatar`, `users`, `owner` or nested `settings`), so offline — where
  // `getChat` cannot fill the gap — the strict read returned null and the
  // header rendered blank. `returnPartialData` hands back what the cache does
  // hold: `name`, `avatarUrl`, `isGroupChat`, enough for a real header. Every
  // consumer already optional-chains the rest.
  //
  // `Partial<>` is shallow, and that under-states what a partial read can
  // hand back: nested values are missing just as often as top-level ones —
  // `settings`, and each element of `users` — while their types still say
  // every field is present. Optional-chain all the way down, never just the
  // first hop.
  const cached = client?.cache.readFragment<Partial<GetChatDetailFragment>>({
    id: `Chat:${chatId}`,
    fragment: GetChatDetailFragmentDoc,
    fragmentName: 'GetChatDetail',
    returnPartialData: true,
  });

  // A dangling reference (no entity for this id at all) reads back as an empty
  // object rather than null once partial data is allowed, so keep the original
  // "nothing cached" contract by checking for the one field that must be there.
  const chat = cached?.id ? cached : null;

  return { chat };
};
